import {AxiosRequestConfig, AxiosError} from 'axios';
import {BaseQueryFn} from '@reduxjs/toolkit/dist/query';
import {traceAxiosInstance} from './trace.axios';
import getFirebaseTokenId from './utils/getFirebaseToken';

export const traceBaseQueryFn: BaseQueryFn<
  {
    url: string;
    baseUrl?: string;
    method?: AxiosRequestConfig['method'];
    body?: AxiosRequestConfig['data'];
    params?: AxiosRequestConfig['params'];
    apiVersion?: string;
    contentType?: string;
    noAuth?: boolean;
    noSession?: boolean;
    headers?: Record<string, string | undefined>;
  },
  unknown,
  unknown,
  {}
> = async (
  {
    url,
    method,
    body: data,
    params,
    baseUrl,
    apiVersion = '2',
    contentType,
    noAuth,
    noSession,
    headers: customHeaders,
  },
  {signal},
) => {
  try {
    const token = await getFirebaseTokenId();
    const sessionId = localStorage.getItem('sessionId');

    const headers: Record<string, string> = {};
    
    // Only add Authorization if noAuth is false and token exists
    if (!noAuth && token) {
      headers.Authorization = 'Bearer ' + token;
    }
    
    // Add Content-Type if specified
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    
    // Add session ID if it exists and not explicitly disabled
    if (sessionId && !noSession) {
      headers['x-session-id'] = sessionId;
    }
    
    // Merge custom headers, filtering out undefined values
    if (customHeaders) {
      Object.entries(customHeaders).forEach(([key, value]) => {
        if (value !== undefined) {
          headers[key] = value;
        }
      });
    }

    const result = await traceAxiosInstance({
      url,
      baseURL: baseUrl,
      method,
      data,
      params,
      signal,
      headers,
    });

    return {
      data: result.data,
      error: undefined,
    };
  } catch (axiosError) {
    const err = axiosError as AxiosError;

    if (err.name === 'CanceledError' || err.message === 'canceled') {
      return {
        error: {status: 'ABORT_ERROR', data: 'Request was aborted'},
      };
    }
    // Handle 401 errors that might be related to session issues
    if (err.response?.status === 401) {
      // Session might be invalid or expired
      // We'll handle this in the SessionContext
    }

    return {
      error: {status: err.response?.status, data: err.response?.data},
      data: undefined,
    };
  }
};
